import { useState } from 'react';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  RadioGroup,
  RadioGroupItem,
  useIsMobile,
} from '@design-system';

const PERSONA_OPTIONS = [
  'Consumer',
  'Wealth Advisor',
  'Insurance Agent',
  'Estate Planner',
  'BGA, FMO, or IMO',
  'Carrier',
  'Benefits Broker',
] as const;

type PersonaOption = (typeof PERSONA_OPTIONS)[number];

interface BookDemoPersonaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookCall: () => void;
}

const PERSONA_HEADING = 'How will you be using Waterlily?';
const PERSONA_DESCRIPTION = "We'll tailor your experience based on your role";

export function BookDemoPersonaModal({
  open,
  onOpenChange,
  onBookCall,
}: BookDemoPersonaModalProps) {
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState<PersonaOption | ''>('');

  const radioList = (
    <RadioGroup
      value={selected}
      onValueChange={value => setSelected(value as PersonaOption)}
      className="flex flex-col gap-2"
    >
      {PERSONA_OPTIONS.map(persona => {
        const isChecked = selected === persona;
        return (
          <label
            key={persona}
            htmlFor={`persona-${persona.replace(/\s/g, '-')}`}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
              isChecked
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 bg-white hover:border-gray-300',
            )}
          >
            <RadioGroupItem
              value={persona}
              id={`persona-${persona.replace(/\s/g, '-')}`}
            />
            <span className="text-foreground text-sm font-medium">{persona}</span>
          </label>
        );
      })}
    </RadioGroup>
  );

  const submitButton = (
    <Button onClick={onBookCall} disabled={selected === ''} size="lg" className="w-full">
      Book a Call
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="flex max-h-[90vh] flex-col overflow-hidden px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-xl font-bold text-foreground">
              {PERSONA_HEADING}
            </DrawerTitle>
            <DrawerDescription className="text-foreground text-sm">
              {PERSONA_DESCRIPTION}
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto py-4">{radioList}</div>
          <DrawerFooter className="pt-2">{submitButton}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="px-8 pb-2 pt-8 text-center">
          <DialogTitle className="text-center text-2xl font-bold text-foreground">
            {PERSONA_HEADING}
          </DialogTitle>
          <DialogDescription className="mt-1 text-center text-foreground text-sm">
            {PERSONA_DESCRIPTION}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4">{radioList}</div>
        <DialogFooter className="px-6 pb-8 pt-4">{submitButton}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
